import { actions } from '..';
import { HttpError, parseMsDate } from '../../api';
import * as apiQueue from '../../api/queue-control';
import { addTaskItem } from '../../api/task-control';
import { QueuingState, Recipient, TaskItemAction, CallType } from '../../model';
import { selectMyRegion, selectMyself } from '../reducers/config';
import { selectEmail, selectReplyEmail } from '../reducers/emails';
import { selectMedia } from '../reducers/media';
import { all } from 'async-saga';
import { takeEvery, waitForState } from '../tools/async-saga-types';
import { getOutGoing, isOutGoingReady } from '../reducers/localSettings';
import { selectAdvancedSearchQueueEmails } from '../reducers/queueEmails';

const fetchEmailRead = takeEvery(actions.fetchEmailReadRequest, async function (ctx, { mediaId, emailId, accountId }) {
  try {

    const email = await apiQueue.getQueueEmailByEmailId(emailId, accountId)
    const id = !mediaId ? emailId : mediaId

    const state = ctx.getState();
    const accounts = selectAdvancedSearchQueueEmails(state)

    if (accounts === null) {
      console.error('selectQueueEmails is not loaded!!!!!')
      ctx.dispatch(actions.showClientNotification('Queue accounts are not loaded so can\'t retrieve email'))
      return;
    }

    //hack in queue details for Azure search 
    let queueId = email.queueId
    let queueDescription = email.queueDescription
    let signatureTemplatePath = email.signatureTemplatePath
    let defaultEmailFontFamily = email.defaultEmailFontFamily

    if (accountId !== null) {
      const account = accounts.find(a => a.emailAddress === accountId)
      if (account) {
        queueId = account.queueId
        queueDescription = account.queueDescription
        signatureTemplatePath = account.signatureTemplatePath
        defaultEmailFontFamily = account.defaultEmailFontFamily
      }
    }

    ctx.dispatch(actions.fetchEmailReadResponse(id, {
      ...email,
      queueId: queueId ? queueId : '',
      queueDescription: queueDescription ? queueDescription : '',
      signatureTemplatePath: signatureTemplatePath ? signatureTemplatePath : '',
      defaultEmailFontFamily: defaultEmailFontFamily ? defaultEmailFontFamily : '',
      sentAtUtc: parseMsDate(email.sentAtUtc),
      receivedAtUtc: parseMsDate(email.receivedAtUtc)!,
    }, accountId))

  } catch (error) {
    if (error instanceof HttpError) {
      if (error.status === 500) {
        ctx.dispatch(actions.showClientNotification('There has been a server error requesting the media'))
      } else if (error.status === 403) {
        ctx.dispatch(actions.showClientNotification('Access denied'))
      }
    }
  }
})

const replyToEmail = takeEvery(actions.replyToEmail, async function (ctx, { mediaId, type }) {
  const state = ctx.getState();
  const email = selectEmail(state, mediaId)
  if (!email || email && email.reply) { return }

  const { id, queueId, signatureTemplatePath, defaultEmailFontFamily } = email.original
  let reply = await apiQueue.newReplyEmail(id, mediaId, queueId, type, signatureTemplatePath, defaultEmailFontFamily)

  if (email.accountId) {

    reply = {
      ...reply,
      sender: {
        address: email.accountId,
        name: '',
      },
    }
  }

  ctx.dispatch(actions.setReplyEmail(mediaId, reply, false))
})

const onRequetNewOutGoingEmail = takeEvery(actions.requestNewOutgoing, async function (ctx, { email }) {

  ctx.dispatch(actions.setNewOutgoing(email))

  // this will create a call (2057)
  const data = await apiQueue.requestOutGoingEmail(email.queueId.toString())

  await waitForState(ctx, s => selectMedia(s, data.mediaId) !== undefined)

  //change view
  ctx.dispatch(actions.openAgentView(data.mediaId))

  if (!data.mediaId) {
    ctx.dispatch(actions.showClientNotification('There has been an error creating the email'))
  } else {
    ctx.dispatch(actions.setNewOutgoing({ ...email, id: data.id, mediaId: data.mediaId }))
  }
})


const onRequestCreateNewOutGoing = takeEvery(actions.requestCreateNewOutGoing, async function (ctx, { mediaId }) {
  const state = ctx.getState();
  const media = selectMedia(state, mediaId)
  const request = getOutGoing(state)

  if (!media || !request) return

  await waitForState(ctx, s => isOutGoingReady(s))

  const email = getOutGoing(ctx.getState())

  if (!email || !email.id || !email.mediaId) {
    ctx.dispatch(actions.showClientNotification('There has been an error creating the email email.  Please kill the call and try again.'))
    return
  }

  const reply = await apiQueue.newOutGoingEmail(email.id, email.mediaId, email.queueId.toString(), email.signatureTemplatePath, email.defaultEmailFontFamily, email.emailAddress)

  //setup original email
  const recipient: Recipient = { name: '', address: '' }
  const original = {
    clientPath: '',
    id: email.id,
    itemId: email.id,
    originalId: email.id,
    originalItemId: email.id,
    subject: '',
    receivedAtUtc: Date.now(),
    conversationId: '',
    from: recipient,
    sender: recipient,
    receivedBy: recipient,
    toRecipients: [],
    ccRecipients: [],
    attachments: [],
    body: '',
    style: 0,
    extensionId: '',
    extensionDescription: '',
    isOutGoing: 1,
    isAnswered: 0,
    queueId: '',
    queueDescription: '',
    queuingState: QueuingState.None,
    signatureTemplatePath: '',
    defaultEmailFontFamily: email.mediaId,
  }

  ctx.dispatch(actions.setCreateNewOutGoing(email.mediaId, original, { ...reply, datastoreItemId: email.mediaId }))

})

const sendEmail = takeEvery(actions.sendEmail, async function (ctx, mediaId) {
  const state = ctx.getState();
  const email = selectReplyEmail(state, mediaId)
  const media = selectMedia(state, mediaId)
  const myself = selectMyself(state)
  const region = selectMyRegion(state)

  if (!email || !myself || !region) { return }
  const { to, cc, ...reply } = email

  reply.toRecipients = []
  reply.ccRecipients = []

  if (to.indexOf(';') > -1) {
    to.split(';').forEach(e => {
      if (e.indexOf(',') > -1) {
        e.split(',').forEach(e => {
          if (e !== "")
            reply.toRecipients.push({ name: '', address: e.trim() })
        })
      } else {
        if (e !== "")
          reply.toRecipients.push({ name: '', address: e.trim() })
      }
    })
  } else {
    to.split(',').forEach(e => {
      if (e !== "")
        reply.toRecipients.push({ name: '', address: e.trim() })
    })
  }

  if (cc.indexOf(';') > -1) {
    cc.split(';').forEach(e => {
      if (e.indexOf(',') > -1) {
        e.split(',').forEach(e => {
          if (e !== "")
            reply.ccRecipients.push({ name: '', address: e.trim() })
        })
      } else {
        if (e !== "")
          reply.ccRecipients.push({ name: '', address: e.trim() })
      }
    })
  } else {
    cc.split(',').forEach(e => {
      if (e !== "")
        reply.ccRecipients.push({ name: '', address: e.trim() })
    })
  }

  //validate email addresses
  let invalid = ''
  const emails = reply.toRecipients.map(_=>_.address).concat(reply.ccRecipients.map(_=>_.address))
  emails.forEach(e => {
    if (!validateEmailAddress(e)) {
      invalid = e
    }
  })
  if (invalid !== '') {
    ctx.dispatch(actions.sendEmailFailed(mediaId))
    ctx.dispatch(actions.showClientNotification('Invalid email address - ' + invalid))
    return
  }

  const result = await apiQueue.sendQueueEmail(email.queueId, reply, mediaId)
  if (result.success && !result.error) {
    ctx.dispatch(actions.emailSent(mediaId))
    ctx.dispatch(actions.showClientNotification('Email sent'))

    //need to task reply also if original email has been tasked for queued email call
    if (media && media.taskId !== '' && media.mediaType === 'EMAIL' && media.callType === CallType.Queue) {
      await addTaskItem(myself, media.taskId, email.id, 'EMAIL', email.subject, TaskItemAction.outgoing, mediaId)
      //removed an using tblTaskEmail broadcasts as this will not show the new task item
      //ctx.dispatch(actions.fetchMediaTaskItemsRequest(media.id, media.taskId))
    }
  } else {

    ctx.dispatch(actions.sendEmailFailed(mediaId))
    ctx.dispatch(actions.showClientNotification('Email failed to send. ' + result.error))

  }
})

function validateEmailAddress(value: string) {
  const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

  if (value.match(validRegex)) {
    return true;
  } else {
    return false;
  }
}

const setEmailSubject = takeEvery(actions.setEmailSubject, async function (ctx, { subject, mediaId }) {
  const state = ctx.getState();
  const myself = selectMyself(state)
  const email = selectReplyEmail(state, mediaId)
  const media = selectMedia(state, mediaId)

  if (!email || !media || !myself || media.callType !== CallType.Outgoing) { return }

  ctx.dispatch(actions.updateMediaSubject(mediaId, subject))

  if (media.taskId !== '') {
    ctx.dispatch(actions.updateMediaItemTaskSubject(media.clidObjectKey, media.taskId, email.id, subject))
  }
})

const onCancelReply = takeEvery(actions.cancelReply, async function (ctx, payload) {
  const state = ctx.getState();
  const email = selectReplyEmail(state, payload)
  const media = selectMedia(state, payload)

  if (!email || !media) { return }

  if (media.callType === CallType.Outgoing)
    await apiQueue.cancelEmail(email.id)
})

export default all(
  fetchEmailRead,
  replyToEmail,
  onRequetNewOutGoingEmail,
  sendEmail,
  setEmailSubject,
  onRequestCreateNewOutGoing,
  onCancelReply,
)
